(ns frontend.view.entity-viewers.activity.entity-activity
  (:require
   [basic-components.field-editor :as field-editor]
   [clojure.string :as string]
   [common.date-formats :as date-formats]
   [common.dates :as dates]
   [common.entity :as entity]
   [common.features.plan :as plan]
   [common.localization :as loc]
   [common.schema.registry :as registry]
   [common.timezones :as timezones]
   [common.util :as cu]
   [common.value-creation-status :as value-creation-status]
   [frontend.account :as account]
   [frontend.actions.attachment :as attachment-actions]
   [frontend.actions.email :as actions-email]
   [frontend.actions.entity :as entity-actions]
   [frontend.logging :as logging]
   [frontend.routing :as routing]
   [frontend.state.current-session :as session]
   [frontend.utils :as fu]
   [frontend.view.entity-viewers.activity.events :as events]
   [frontend.view.entity-viewers.activity.insider-activity :as insider-activity]
   [frontend.view.modals.activity-entity-editor.view :as activity-entity-editor]
   [frontend.view.modals.email-editor.view :as email-editor-modal]
   [frontend.view.modals.email-viewer.events :as email-events]
   [frontend.view.modals.email-viewer.view :as email-viewer-modal]
   [frontend.view.modals.entity-audit-trail.view :as audit-trail-modal]
   [frontend.view.modals.events :as modals]
   [frontend.view.modals.textarea.view :as textarea-modal]
   [frontend.view.modals.value-creation.view :as value-creation]
   [oops.core :as oops]
   [potpuri.core :as p]
   [reagent.core :as r]
   [ui.components.button.view :as button]
   [ui.components.ck-editor.view :as ck-editor]
   [ui.components.radix-ui.dropdown-menu.view :as dropdown-menu]
   [ui.components.radix-ui.popover.view :as popover]
   [ui.dom-globals :as dg]
   [ui.util :as uu]))

(def content-attributes
  {:entity.type/note            [:note/message]
   :entity.type/note-message    [:note-message/message]
   :entity.type/note-call       [:note-call/message]
   :entity.type/note-meeting    [:note-meeting/message]
   :entity.type/email           [:email/snippet :email/body]
   :entity.type/system-activity nil                                                                 ;; no-content
   })

(def expanded-content-attributes
  {:entity.type/note            [:note/message]
   :entity.type/note-message    [:note-message/message]
   :entity.type/note-call       [:note-call/message]
   :entity.type/note-meeting    [:note-meeting/message]
   :entity.type/email           [:email/body]
   :entity.type/system-activity nil                                                                 ;; no-content
   })


(defn parse-email-date
  [str-date]
  ;; `when`, not `when-not`, and `non-empty-string?`, not `empty-string`.
  ;; cu/empty-string is (or s ""), which is truthy for every input including
  ;; nil, so the old guard made this whole function return nil always and the
  ;; parse below had never once run. action-ts therefore fell through to
  ;; :common/created-on for any email carrying a string :email/date, i.e. it
  ;; ordered them by when the row was created rather than when the mail was
  ;; sent. See ai/mistakes.md.
  (when (cu/non-empty-string? str-date)
    ;;rfc-2822-date-format e.g. "Mon, 15 Apr 2019 09:07:50 GMT"
    (let [pattern "EEE, dd MMM yyyy HH:mm:ss Z"
          dt-val (dates/date str-date {:pattern pattern})
          ms-val (dg/date->millis dt-val)]
      ;; dates/date hands back its seed object when the string does not match
      ;; the pattern - goog's DateTimeParse reports the failure in a return
      ;; value that dates/date discards - and that seed is year 0, a large
      ;; negative millis that would sort the entry above everything else.
      ;; Returning nil instead keeps action-ts on the :common/created-on
      ;; fallback it has been using all along for these.
      (when (and ms-val (pos? ms-val))
        ms-val))))

(defn action-ts
  [entity]
  (let [explicit (case (:entity/type entity)
                   :entity.type/note-call (:note-call/timestamp entity)
                   :entity.type/note-meeting (:note-meeting/timestamp entity)
                   :entity.type/note-message (:note-message/timestamp entity)
                   :entity.type/email (cond (number? (:email/date-ts entity))
                                            (:email/date-ts entity)
                                            (number? (:email/date entity))
                                            (:email/date entity)
                                            (string? (:email/date entity))
                                            (parse-email-date (:email/date entity))
                                            :else
                                            nil)
                   nil)]
    (or explicit (:common/created-on entity))))

(defn action-tz?
  [entity]
  (case (:entity/type entity)
    :entity.type/note-call false
    :entity.type/note-meeting false
    :entity.type/note-message false
    :entity.type/email (cond (number? (:email/date entity))
                             false
                             (string? (:email/date entity))
                             true
                             :else
                             true)
    true))

(defn pass-or-on-hold-meta
  [text entity]
  (let [comment-text (string/trim (str (:system-activity/comment
                                        entity)))]
    [:span
     {:class "flex gap-2 items-center *:py-0"}
     text
     (when (or (not (session/is-read-only-user?))
               (seq comment-text))
       [button/secondary-open
        {:tooltip (string/trim (str (:system-activity/comment
                                     entity)))}
        [:i
         {:class-name (fu/cs :system-activity-comment-icon
                             :fal
                             (if-not (empty? comment-text)
                               :fa-comment
                               :fa-comment-plus))
          :on-click   (fn [_]
                        (when-not (or (session/is-guest?)
                                      (session/is-read-only-user?))
                          (modals/open-modal!
                           textarea-modal/modal-key
                           {:close-modal! identity
                            :title-text   (loc/tr :edit)
                            :button-text  (loc/tr :ok)
                            :value        (:system-activity/comment
                                           entity)
                            :on-submit!   (fn [updated-text]
                                            (entity-actions/*->field-update!
                                             (:db/id entity)
                                             :system-activity/comment
                                             updated-text
                                             (fn [_]
                                               #_(logging/warn "Success"))
                                             (fn [_]
                                               (logging/error "Failed to update activity comment"))))})))}]])]))

(defn meta-text
  [entity-type entity]
  (let [creator-name (account/actor-display-name (:common/created-by entity))
        merge-info (some-> entity
                           :system-activity/info)
        merge-entity-type-name-lc (some-> merge-info
                                          :entity-type
                                          loc/tr
                                          string/lower-case)
        merge-source (some-> entity
                             :system-activity/info
                             :source-display-string)
        caller-name (account/actor-display-name (:note-call/call-made-by entity))
        email-sender (string/lower-case (str (:email/from entity)))
        email-actor-name
        (if (= email-sender (string/lower-case (str (:account/email @session/account))))
          (loc/tr :you)
          (or (->> (concat @session/all-accounts @session/all-persons)
                   (keep (fn [a-or-p]
                           (when (= email-sender
                                    (string/trim
                                     (str (or (:account/email a-or-p)
                                              (:person/universal-email-address a-or-p)))))
                             (entity/display-string a-or-p))))
                   (first))
              email-sender))]
    (case entity-type
      :entity.type/note (loc/tr :activity-label-note
                          [creator-name])
      :entity.type/value-creation (loc/tr :activity-label-value-creation-activity
                                    [creator-name])
      :entity.type/email (loc/tr :activity-label-email [email-actor-name])
      :entity.type/note-call (if (= (:note-call/type entity) :inbound)
                               (loc/tr :activity-label-note-call-inbound [creator-name])
                               (loc/tr :activity-label-note-call-outbound
                                 [(or caller-name creator-name)]))
      :entity.type/note-meeting (loc/tr :activity-label-note-meeting
                                  [creator-name])
      :entity.type/system-activity
      (case (:system-activity/type entity)
        :system-activity.type/pass [pass-or-on-hold-meta
                                    (loc/tr :activity-label-system-activity-pass
                                      [(->> (:system-activity/pass-reason entity)
                                            (get @session/entities)
                                            (entity/display-string))
                                       creator-name])
                                    entity]
        :system-activity.type/put-on-hold [pass-or-on-hold-meta
                                           (loc/tr :activity-label-system-activity-put-on-hold
                                             [(fu/display-date-timestamp
                                               (:system-activity/on-hold-until entity))
                                              creator-name])
                                           entity]
        :system-activity.type/reactivate (loc/tr :activity-label-system-activity-reactivate
                                           [creator-name])
        :system-activity.type/create (loc/tr :activity-label-system-activity-create
                                       [creator-name])
        :system-activity.type/loss [pass-or-on-hold-meta
                                    (loc/tr :activity-label-system-activity-loss
                                      [creator-name])
                                    entity]
        :system-activity.type/won
        (let [related-entities (->> entity
                                    :common/related-entities
                                    (map (fn [id]
                                           (get @session/entities id))))
              fund-commitment (or (->> related-entities
                                       (cu/only-entity :entity.type/fund-commitment)
                                       first
                                       (get @session/entities))
                                  (->> related-entities
                                       (cu/only-entity :entity.type/fundraising-opportunity)
                                       first
                                       :fundraising-opportunity/fund-commitment
                                       (get @session/entities)))
              won-by (->> fund-commitment
                          :fund-commitment/won-by
                          (get @session/entities))
              won-by-name (entity/display-string won-by)]
          (loc/tr :activity-label-system-activity-won
            [(or won-by-name creator-name)]))
        :system-activity.type/stage-change (loc/tr :activity-label-system-activity-stage-change
                                             [(->> (:system-activity/target-stage entity)
                                                   (get @session/entities)
                                                   (entity/display-string))
                                              creator-name])
        :system-activity.type/entity-merge (loc/tr :activity-label-system-activity-entity-merge
                                             [merge-source
                                              merge-entity-type-name-lc])
        :system-activity.type/oppo-move nil                                                         ;; do not show in activity timeline
        (logging/warn "No activity type for " (:db/id entity)))
      :entity.type/note-message (loc/tr :activity-label-note-message
                                  [creator-name
                                   (case (:note-message/message-type entity)
                                     :message.type/other ""                                         ;; do not show type for other
                                     (loc/tr (:note-message/message-type entity)))])
      (str "Activity meta text missing : " entity-type))))

(defn dropdown-menu-item
  [key icon label on-select!]
  ^{:key (str key)}
  [:div
   {:on-click on-select!
    :class    "flex gap-2 items-center"}
   [:i {:class (fu/cs :fal icon)}]
   [:span label]])

(defn dropdown
  [local-entities nev-entity-id entity]
  (let [action-id (:db/id entity)
        menu-actions {:open                   (dropdown-menu-item :open
                                                                  :fa-eye
                                                                  (loc/tr :view)
                                                                  #(modals/open-modal!
                                                                    email-viewer-modal/modal-key
                                                                    {:email-id         (:db/id entity)
                                                                     :local-entities   local-entities
                                                                     :viewed-entity-id nev-entity-id}))
                      :forward                (dropdown-menu-item :forward
                                                                  :fa-forward
                                                                  (loc/tr :forward)
                                                                  #(email-events/forward nev-entity-id entity))
                      :reply                  (dropdown-menu-item :reply
                                                                  :fa-reply
                                                                  (loc/tr :reply)
                                                                  #(email-events/reply nev-entity-id entity))
                      :reply-all              (dropdown-menu-item :reply-all
                                                                  :fa-reply-all
                                                                  (loc/tr :reply-all)
                                                                  #(email-events/reply nev-entity-id entity))
                      :delete                 (dropdown-menu-item :delete
                                                                  :fa-trash
                                                                  (loc/tr :delete)
                                                                  #(events/dropdown-remove-selected local-entities
                                                                                                    action-id
                                                                                                    nev-entity-id
                                                                                                    identity
                                                                                                    identity))
                      :edit-date              (dropdown-menu-item :edit-date
                                                                  :fa-calendar
                                                                  (loc/tr :edit-date)
                                                                  (fn [_]
                                                                    (events/start-edit-date nev-entity-id entity)))
                      :exclude-from-reporting (dropdown-menu-item :exclude-from-reporting
                                                                  (if (:common/exclude-from-reporting? entity)
                                                                    :fa-plus
                                                                    :fa-ban)
                                                                  (if (:common/exclude-from-reporting? entity)
                                                                    (loc/tr :include-in-reporting)
                                                                    (loc/tr :exclude-from-reporting))
                                                                  (fn [_]
                                                                    (events/exclude-from-reporting nev-entity-id entity)))
                      :history                (dropdown-menu-item :history
                                                                  :fa-clock
                                                                  (loc/tr :show-history)
                                                                  (fn [_]
                                                                    (modals/open-modal!
                                                                     audit-trail-modal/modal-key
                                                                     {:title               (loc/tr :audit-trail [(entity/display-string entity)])
                                                                      :entity-ids          [action-id]
                                                                      :primary-entity-type (:entity/type entity)
                                                                      :primary-entity-id   action-id})))
                      :edit                   (dropdown-menu-item :edit
                                                                  :fa-edit
                                                                  (loc/tr :edit)
                                                                  (fn [_]
                                                                    (if (= :entity.type/value-creation (:entity/type entity))
                                                                      (modals/open-modal! value-creation/modal-key
                                                                                          {:containing-entity-id nev-entity-id
                                                                                           :entity-id            action-id})
                                                                      (modals/open-modal! activity-entity-editor/modal-key
                                                                                          {:entity-id        action-id
                                                                                           :viewed-entity-id nev-entity-id
                                                                                           :primary-button-text
                                                                                           (when (= :entity.type/email
                                                                                                    (:entity/type entity))
                                                                                             (loc/tr :submit))}))))}
        related-actions (cond (= :system-activity.type/create (:system-activity/type entity))
                              nil
                              (= (:entity/type entity) :entity.type/system-activity)
                              [:edit-date :exclude-from-reporting :history]
                              (not= (:entity/type entity) :entity.type/email)
                              [:edit :delete :history]
                              ;; email >>
                              (and (plan/available :feature/nylas-integration @session/tenant @session/role)
                                   (:account/nylas-account-id @session/account))
                              [:open :forward :reply :reply-all :delete :pin :history]
                              :else
                              [:open :delete :pin :history])]
    (when related-actions
      [dropdown-menu/view
       {:trigger [dropdown-menu/button-trigger-open
                  [:<>
                   [:i.fas.fa-ellipsis-v]]]
        :items   (->> (select-keys menu-actions related-actions)
                      (mapv (fn [action-item]
                              {:value        (second action-item)
                               :custom-item? true}))
                      (doall))}])))


(defn attachments-dropdown
  [nev-entity-id attachments]
  (let [nev-entity (get @session/entities nev-entity-id)
        entity-attachments (:common/attachments nev-entity)]
    [dropdown-menu/view
     {:trigger [dropdown-menu/button-trigger-open
                [:i.fas.fa-paperclip]]
      :items   (->> attachments
                    (mapv (fn [attachment]
                            (let [attachment-id (:db/id attachment)]
                              {:value
                                             ^{:key (str "attachment-" attachment-id)}
                                             [:div
                                              {:on-click   (fn [e]
                                                             (let [target (dg/target e)
                                                                   tag (oops/oget target "tagName")]
                                                               (when-not (= tag "I")
                                                                 (fu/open-attachment! attachment-id))))
                                               :class-name ""}
                                              [:span (:attachment/filename attachment)]
                                              [:span
                                               {:class "flex justify-between items-center gap-2"}
                                               [button/square-secondary-open
                                                {:on-click #(fu/open-attachment! attachment-id)
                                                 :tooltip  (loc/tr :open)}
                                                [:i.fal.fa-external-link]]
                                               [button/square-secondary-open
                                                {:on-click #(fu/download-attachment! attachment-id)
                                                 :tooltip  (loc/tr :download)}
                                                [:i
                                                 {:class (fu/cs :fal
                                                                :fa-cloud-download
                                                                (when-not (some #{attachment-id} entity-attachments)
                                                                  :mr-1))}]]
                                               (when-not (or (session/is-read-only-user?)
                                                             (some #{attachment-id} (:common/attachments nev-entity)))
                                                 [button/square-secondary-open
                                                  {:tooltip (loc/tr :copy-to-specific-entity-attachments
                                                              [(cu/str-trim-lcase (loc/tr (:entity/type nev-entity)))])}
                                                  [:i.fal.fa-file-upload
                                                   {:on-click (fn [_e]
                                                                (attachment-actions/*->copy-to attachment-id
                                                                                               nev-entity-id
                                                                                               (fn [_])
                                                                                               (fn [_])))}]])
                                               (when (and (plan/available :feature/nylas-integration @session/tenant @session/role)
                                                          (:account/nylas-account-id @session/account))
                                                 [button/square-secondary-open
                                                  {:on-click (fn [_e]
                                                               (modals/open-modal!
                                                                email-editor-modal/modal-key
                                                                {:title-text         (loc/tr :send-file)
                                                                 :entity-id          nev-entity
                                                                 :attachments        #{attachment-id}
                                                                 :on-primary-submit! actions-email/*->send-email}))}
                                                  [:i.fal.fa-share]])]]
                               :custom-item? true})))
                    (doall))}]))

(defn activity-elem
  [{:keys [state _local-entities _viewed-entity-id entity]}]
  (r/create-class
   {:component-did-mount    (fn [])
    :component-will-unmount (fn []
                              (swap! state p/dissoc-in [:content-height (:db/id entity)]))
    :reagent-render
    (fn [{:keys [state local-entities viewed-entity-id entity]}]
      (let [last-updated-by (:common/updated-by entity)
            last-updated-on (:common/updated-on entity)
            df (or (:tenant/date-format @session/tenant) (loc/tr :date-format))
            tz (or (:tenant/timezone @session/tenant) timezones/default-timezone)
            created-on (:common/created-on entity)
            entity-type (:entity/type entity)
            title-text (meta-text entity-type entity)
            privacy-status (when (and (= entity-type :entity.type/email)
                                      (= (:email/owner entity) (:db/id @session/account)))
                             (or (:email/privacy-status entity)
                                 :privacy-status/public))
            activity-entity-id (:db/id entity)

            override-expand-entity (:override-expand-entity @state)

            expanded? (if (contains? override-expand-entity activity-entity-id)
                        (get override-expand-entity activity-entity-id)
                        (:expand-all? @state))

            content-attributes (if expanded?
                                 (get expanded-content-attributes entity-type)
                                 (get content-attributes entity-type))
            meeting? (= entity-type :entity.type/note-meeting)
            participants (when meeting?
                           (->> entity
                                :note-meeting/participants
                                (keep session/get-entity)))
            attachment-ids (:common/attachments entity)
            attachments (->> attachment-ids
                             (map (fn [aid]
                                    (or (get @local-entities aid)
                                        (session/get-entity aid))))
                             (doall))
            other-related-entities
            (->>
             (set (:common/related-entities entity))
             (keep
              (fn [id]
                (let [r-e (get @session/entities id)]
                  (when
                   (and (not= id viewed-entity-id)
                        (some #{(:entity/type r-e)}
                              [:entity.type/person
                               :entity.type/company
                               :entity.type/investor
                               :entity.type/oppo
                               :entity.type/holding
                               :entity.type/sale-agreement
                               :entity.type/fundraising-opportunity
                               :entity.type/sales-opportunity
                               :entity.type/exit-opportunity
                               :entity.type/fund-commitment
                               :entity.type/fund
                               :entity.type/real-estate
                               :entity.type/fof-asset
                               :entity.type/other-asset]))
                    r-e))))
             (remove
              (fn [e]
                (let [entity-type (:entity/type e)
                      no-fundraising? (not (session/fundraising?))
                      no-portfolio? (not (session/portfolio?))
                      no-fund-management? (not (session/has-feature-access? :feature/fund-management))
                      no-real-estate? (not (session/has-feature-access?
                                            :feature/asset-types-real-estate))
                      no-company? (not (session/has-feature-access? :feature/asset-types-company))
                      no-fof-asset? (not (session/has-feature-access? :feature/asset-types-fof))
                      no-other-asset? (not (session/has-feature-access? :feature/asset-types-other))
                      no-sales-pipelines? (not (session/sales-pipelines?))
                      no-exit-process-management? (not (session/exit-process-management?))
                      no-dealflow? (not (session/dealflow?))]
                  (case entity-type
                    :entity.type/fund-commitment
                    no-fundraising?
                    :entity.type/fund
                    (or no-fundraising?
                        no-fund-management?)
                    :entity.type/fundraising-opportunity
                    no-fundraising?
                    :entity.type/sales-opportunity
                    no-sales-pipelines?
                    :entity.type/sale-agreement
                    no-sales-pipelines?
                    :entity.type/exit-opportunity
                    no-exit-process-management?
                    :entity.type/holding
                    no-portfolio?
                    :entity.type/investor
                    (and no-portfolio? no-fundraising?)
                    :entity.type/real-estate
                    no-real-estate?
                    :entity.type/company
                    no-company?
                    :entity.type/fof-asset
                    no-fof-asset?
                    :entity.type/other-asset
                    no-other-asset?
                    :entity.type/oppo
                    no-dealflow?
                    false)))))
            tags (:activity/tag-tags entity)
            on-card-click
            (fn [e]
              (dg/prevent-and-stop e)
              (let [target (dg/target e)
                    classes (oops/oget target "classList")
                    values (string/split (oops/oget classes "value") #" ")
                    is-dropdown-option? (contains? (set values) "dropdown-item")
                    is-attachment-button? (some #{"dropdown-toggle" "fa-paperclip"} (set values))]
                (when-not (or is-dropdown-option?
                              is-attachment-button?
                              (get-in @state [:prevent-click? activity-entity-id]))
                  (swap! state assoc-in [:override-expand-entity activity-entity-id] (not expanded?)))))
            on-card-mouse-down
            (fn [e]
              (let [target (dg/target e)
                    input-or-button? (dg/has-tag-name-in-dom-path target ["input" "button"])
                    classes (oops/oget target "classList")
                    values (string/split (oops/oget classes "value") #" ")
                    is-toggle (contains? (set values) "toggle-dropdown-el")]
                (when (and (not is-toggle)
                           (not input-or-button?))
                  (swap! state assoc-in [:prevent-click? activity-entity-id] false)
                  (js/setTimeout (fn []
                                   (swap! state assoc-in [:prevent-click? activity-entity-id] true))
                                 200))))
            content-height (get-in @state [:content-height (:db/id entity)])
            ;; Must match the collapsed max-height below.
            content-height-limit 75
            has-expandable-content? (< content-height-limit content-height)]
        (when title-text
          [:li
           {:class "pt-4 flex items-baseline gap-3 relative after:content-[''] after:block after:w-px after:bg-base-regular after:absolute after:left-3 after:-bottom-2 after:top-0 after:z-0 first:after:top-0 first:pt-0 [&:nth-last-child(2)]:after:hidden overflow-hidden"
            :key   (str "activity-entry-" (:db/id entity))}
           [:div
            {:class "w-6 relative"}
            [:span
             {:class "w-6 h-6 flex items-center justify-center rounded-full text-xs uppercase text-secondary-regular-text bg-secondary-regular relative z-10"}
             [:i {:class-name (fu/cs :fal (entity/default-icon entity))}]]]
           [:div
            {:class "flex-1 min-w-0"}
            [:div
             {:on-click      on-card-click
              :on-mouse-down on-card-mouse-down}
             [:div
              {:class "flex items-center justify-between gap-2 cursor-pointer text-xs text-base-light-text-paler"}
              [:div
               {:class "flex gap-2"}
               [:span
                (date-formats/format-millis (action-ts entity)
                                            df
                                            (when (action-tz? entity)
                                              tz))]
               [:span
                " | "]
               (when privacy-status
                 [:div
                  {:class "*:p-0"}
                  [button/secondary-open
                   {:tooltip  (if (= privacy-status :privacy-status/private)
                                (loc/tr :privacy-status/private-tooltip)
                                (loc/tr :privacy-status/public-tooltip))
                    :on-click (fn [_]
                                (entity-actions/*->field-update! (:db/id entity)
                                                                 :email/privacy-status
                                                                 (if (= privacy-status
                                                                        :privacy-status/private)
                                                                   :privacy-status/public
                                                                   :privacy-status/private)
                                                                 (fn [_]
                                                                   (logging/warn "Success"))
                                                                 (fn [_]
                                                                   (logging/error
                                                                    "Failed to update email privacy"))))}
                   (if (= privacy-status :privacy-status/private)
                     [:i.fal.fa-user-secret]
                     [:i.fal.fa-users])]])
               [:span
                {:class "flex gap-2 "}
                title-text]
               (when (and last-updated-on
                          last-updated-by
                          ;; hack to hide last updated by in data enrichment
                          (> (- last-updated-on created-on) 2000))
                 [:span
                  (loc/tr :activity-entry-updated-text
                    [(account/actor-display-name last-updated-by :middle-of-sentence? true)
                     (date-formats/format-millis last-updated-on
                                                 df
                                                 tz)])])
               (when-let [insider-group-id (:common/insider-group entity)]
                 (let [insider-group (session/get-entity insider-group-id)]
                   (when insider-group
                     [:div
                      {:class "absolute top-3 right-6"}
                      [popover/view
                       {:open?          (get-in @state [:popover-open? activity-entity-id] false)
                        :on-open-change (fn [open?]
                                          (swap! state assoc-in [:popover-open? activity-entity-id] open?))}
                       (if (= :insider-group.status/public (:insider-group/status insider-group))
                         [:div
                          {:class "py-1 px-2 text-xs bg-base-regular text-base-regular-text rounded-full "}
                          (loc/tr :was-confidential)]
                         [:span
                          {:class "py-1 px-2 text-xs bg-base-regular text-base-regular-text rounded-full "}
                          "Private"])
                       [insider-activity/popup-view insider-group-id]]])))]
              [:div
               {:class "flex items-center gap-2"}
               (when has-expandable-content?
                 [:div
                  (if expanded?
                    [:i.fal.fa-compress-alt]
                    [:i.fal.fa-expand-alt])])

               (when-not (session/is-read-only-user?)
                 [dropdown local-entities viewed-entity-id entity])]]
             (when-not (empty? tags)
               [:div
                (->> tags
                     (map (fn [tag]
                            (let [tag-entity (get @session/entities tag)
                                  title (:named-value/value tag-entity)]
                              [:span
                               {:class-name (fu/cs "zap-badge zap-badge--secondary mr-1 mt-2")
                                :key        (str "note-tag-" tag)}
                               title])))
                     (doall))])
             (when (and (coll? content-attributes)
                        (seq content-attributes))
               [:div
                {:class-name (fu/cs "text-xs group relative"
                                    (when (and (not expanded?) has-expandable-content?)
                                      "max-h-[75px] overflow-y-hidden before:content-[''] before:absolute before:bottom-0 before:left-0 before:right-0 before:h-8 before:z-10 before:pointer-events-none before:bg-gradient-to-b before:from-transparent before:to-base-light"))}
                [:div
                 {:class "[&_.ck-content]:border-0 relative"}
                 (let [content (->> content-attributes
                                    (map (fn [content-attribute]
                                           (get entity content-attribute)))
                                    (remove cu/blank-value?)
                                    (first)
                                    (str)
                                    (string/trim))]
                   (when (not-empty content)
                     ^{:key (str "activity-entry-content" (:db/id entity) "-" expanded?)}
                     [:div
                      {:ref (fn [el]
                              (when el
                                (let [height (oops/oget el "offsetHeight")
                                      prev-height (get-in @state [:content-height (:db/id entity)])]
                                  (when-not (= height prev-height)
                                    (swap! state assoc-in [:content-height (:db/id entity)] height)))))}
                      (let [html-with-links (fu/eml-txt-body->hyperlinks content)
                            content (->> html-with-links
                                         (fu/sanitize-html))]
                        [ck-editor/viewer content])]))]
                [:span {:class "text-base-light-text-accent block cursor-pointer"}]])
             [:div
              {:class "pt-2 flex items-baseline"}
              (when (seq attachments)
                [attachments-dropdown viewed-entity-id attachments])
              (when (and (pos? (count other-related-entities))
                         (not (session/is-guest?)))
                [:div
                 {:class "w-full flex flex-col gap-1"}
                 [:span
                  {:class "text-base-light-text text-xs font-bold"}
                  (loc/tr :associated-also-with)]
                 [:ul
                  {:class "m-0 p-0 flex flex-wrap gap-1 items-center"}
                  (doall
                   (for [rel other-related-entities]
                     [:li
                      {:class    "p-2 inline-flex gap-2 items-center bg-base-regular rounded text-xs cursor-pointer hover:text-base-light-text-accent"
                       :on-click (fn [e]
                                   (dg/prevent-and-stop e)
                                   (routing/open-entity (:db/id rel)))
                       :key      (str "associated-with" (:db/id rel))}
                      [:i {:class (uu/cm :fal (entity/default-icon rel))}]
                      [:span
                       {:class "font-semibold"}
                       (entity/resolve-entity-name rel session/get-entity session/reverse-lookup)]
                      (when-not (session/is-read-only-user?)
                        [:i.fal.fa-times.cursor-pointer
                         {:on-click (fn [e]
                                      (dg/prevent-and-stop e)
                                      (events/remove-action-relationship
                                       local-entities
                                       (:db/id entity)
                                       (:db/id rel)
                                       #(logging/log "SAVED OK")
                                       #(logging/error "Failed to remove related entity : " %)))}])]))]])]
             (when (and meeting?
                        (seq participants))
               [:div
                {:class "flex items-center"}
                [:div
                 [:div
                  [:span
                   {:class "inline-block text-xs text-base-light-text-paler mr-2"}
                   (loc/tr :our-participants)]
                  [:span
                   {:class "text-xs"}
                   (doall
                    (->> participants
                         (map entity/display-string)
                         (string/join ", ")))]]]])]]])))}))

(defn value-creation-elem
  [{:keys [entity class-name state local-entities viewed-entity-id]}]
  (let [tenant @session/tenant
        tenant-schema @session/tenant-schema
        df (or (:tenant/date-format tenant) (loc/tr :date-format))
        tz (or (:tenant/timezone tenant) timezones/default-timezone)
        last-updated-by (:common/updated-by entity)
        last-updated-on (:common/updated-on entity)
        created-on (:common/created-on entity)
        entity-type (:entity/type entity)
        header-title-text (meta-text entity-type entity)
        title (:value-creation/title entity)

        value-creation-entity-id (:db/id entity)

        override-expand-entity (:override-expand-entity @state)

        expanded? (if (contains? override-expand-entity value-creation-entity-id)
                    (get override-expand-entity value-creation-entity-id)
                    (:expand-all? @state))

        categories-label (if (:value-creation/holding-category entity)
                           (loc/tr :value-creation-category
                             [(->> (session/get-entity (:value-creation/holding-category entity))
                                   :named-value/value)])
                           (loc/tr :value-creation-categories
                             [(->> (:value-creation/investor-categories entity)
                                   (map (fn [category-id]
                                          (:named-value/value (session/get-entity category-id))))
                                   (string/join ", "))]))
        attachment-ids (:common/attachments entity)
        attachments (->> attachment-ids
                         (map (fn [aid]
                                (or (get @local-entities aid)
                                    (session/get-entity aid))))
                         (doall))
        status (:value-creation/status entity)
        status-label (value-creation-status/get-label status)
        managers-label (if (:value-creation/our-managers entity)
                         (loc/tr :value-creation-managers
                           [(->> (:value-creation/our-managers entity)
                                 (map fu/display-entity)
                                 (string/join ", "))])
                         (loc/tr :value-creation-manager
                           [(fu/display-entity (:value-creation/our-manager entity))]))
        work-in-progress? (some #{status}
                                [:value-creation.status/work-in-progress
                                 :value-creation.status/almost-finished])
        done? (= status :value-creation.status/done)
        description-and-lessons (:value-creation/description-and-lessons-learned entity)
        on-card-click (fn [e]
                        (dg/prevent-and-stop e)
                        (let [target (dg/target e)
                              classes (oops/oget target "classList")
                              values (string/split (oops/oget classes "value") #" ")
                              is-dropdown-option? (contains? (set values) "dropdown-item")]
                          (when-not (or is-dropdown-option?
                                        (get-in @state [:prevent-click? value-creation-entity-id]))
                            (swap! state assoc-in
                                   [:override-expand-entity value-creation-entity-id]
                                   (not expanded?)))))
        on-card-mouse-down
        (fn [e]
          (let [target (dg/target e)
                classes (oops/oget target "classList")
                values (string/split (oops/oget classes "value") #" ")
                is-toggle (contains? (set values) "toggle-dropdown-el")]
            (when-not is-toggle
              (swap! state assoc-in [:prevent-click? value-creation-entity-id] false)
              (js/setTimeout
               (fn []
                 (swap! state assoc-in [:prevent-click? value-creation-entity-id] true))
               200))))]
    (when title
      [:li
       {:class-name (fu/cs "activity-entry" class-name)
        :key        (str "activity-entry-" (:db/id entity))}
       [:div
        {:class-name (fu/cs "activity-entry-left"
                            (when work-in-progress?
                              "activity-entry-left__in-progress")
                            (when done?
                              "activity-entry-left__done"))}
        [:span.activity-bubble
         [:i {:class-name (fu/cs "fal fa-dollar-sign")}]]]
       [:div.activity-entry-right
        [:div
         {:class-name    (fu/cs :activity-entry-block)
          :on-mouse-down on-card-mouse-down
          :on-click      on-card-click}
         [:div
          {:class "inline-flex justify-between items-center gap-2 text-xs text-base-light-text-paler"}
          [:div
           [:span.activity-entry-header__date
            (date-formats/format-millis (action-ts entity)
                                        df
                                        (when (action-tz? entity)
                                          tz))]
           [:span
            {:class "inline-block px-2"}
            " | "]
           [:span header-title-text]
           (when (and last-updated-on
                      last-updated-by
                      (not= last-updated-on created-on))
             [:span.activty-entry-header__text
              (loc/tr :activity-entry-updated-text
                [(account/actor-display-name last-updated-by :middle-of-sentence? true)
                 (date-formats/format-millis last-updated-on
                                             df
                                             tz)])])]
          (when-let [insider-group-id (:common/insider-group entity)]
            (let [insider-group (session/get-entity insider-group-id)]
              (when insider-group
                [:div
                 {:class ""}
                 [popover/view
                  {:open?          (get-in @state [:popover-open? value-creation-entity-id] false)
                   :on-open-change (fn [open?]
                                     (swap! state assoc-in [:popover-open? value-creation-entity-id] open?))}
                  [:div
                   {:class    (when (= :insider-group.status/public (:insider-group/status insider-group))
                                "py-1 px-2 text-xs bg-base-regular text-base-regular-text rounded-full")
                    :on-click (fn [e]
                                (swap! state update-in [:popover-open? value-creation-entity-id] not)
                                (fu/stop-n-prevent e))}
                   (if (= :insider-group.status/public (:insider-group/status insider-group))
                     (loc/tr :was-confidential)
                     [:i.fas.fa-user-secret])]
                  [insider-activity/popup-view insider-group-id]]])))

          [:div
           {:class "flex items-center gap-2"}
           [:div
            (if expanded?
              [:i.fal.fa-compress-alt.cursor-pointer]
              [:i.fal.fa-expand-alt.cursor-pointer])]
           (when-not (session/is-read-only-user?)
             [dropdown local-entities viewed-entity-id entity])]]
         [:div
          {:class-name (fu/cs "activity-entry-content value-creation-content")}
          [:div
           [:h2.title-h4 title]
           [:div.mt-1
            [:div
             {:class-name (fu/cs (when-not expanded?
                                   "activity-truncated-text"))}
             description-and-lessons]
            (when-not expanded?
              [:span.show-all])]]

          (if expanded?
            [:div.mt-4
             (->> (registry/get-schema tenant-schema :entity.type/value-creation)
                  (remove (fn [[k _]]
                            (or (some #{(namespace k)} ["common" "db"])
                                (some #{k}
                                      [:value-creation/title
                                       :value-creation/description-and-lessons-learned]))))
                  (keep (fn [[k fs]]
                          (let [display-str (fu/display-data tenant-schema tenant k fs entity)]
                            (when-not (cu/blank-value? display-str)
                              (str (loc/field-name k fs) ": " display-str)))))
                  (map-indexed (fn [idx txt]
                                 ^{:key (str "value-creation-attr-" txt)}
                                 [:div
                                  {:class (fu/cs "text-size--sm"
                                                 (when (pos? idx) "mt-1"))}
                                  txt]))
                  (doall))]
            [:div.mt-1
             [:div.text-size--sm categories-label]
             [:div.text-size--sm.mt-1 (loc/tr :value-creation-status [status-label])]
             [:div.text-size--sm.mt-1 managers-label]])
          [:div.activity-entry-footer
           (when (seq attachments)
             [attachments-dropdown viewed-entity-id attachments])]]]]])))

(defn resolve-person-emails
  [local-entities entity-id]
  (->> @local-entities
       (vals)
       (cu/filter-by :entity/type :entity.type/email)
       (filter (fn [email]
                 (some #{entity-id} (:common/related-entities email))))
       (remove (fn [e]
                 (and (= (:email/privacy-status e) :privacy-status/private)
                      (not= (:email/owner e) (:db/id @session/account)))))))

(defn resolve-associated-people-email-ids-for-entity
  [local-entities entity-id]
  (let [associations (fu/related-entity-associations :entity.type/person entity-id)
        related-person-and-email-ids
        (->> associations
             (mapcat (fn [association]
                       (let [person (fu/get-association-party association :entity.type/person)
                             person-emails (resolve-person-emails local-entities (:db/id person))]
                         (->> person-emails
                              (map (fn [pe]
                                     [(:db/id pe) (:db/id person)])))))))]
    related-person-and-email-ids))

(defn filter-by-tags-dropdown
  [options local-state on-change!]
  [:div
   {:class "mt-2"}
   [:div
    [field-editor/view
     {:editor/type  :editor.type/multi-select
      :value        (:selected-tags @local-state)
      :state-atom   local-state
      :placeholder  (loc/tr :filter-activity-by-tag)
      :value-type   :number
      :attribute    :selected-tags
      :label        ""
      :hide-label?  true
      :on-save!     (fn [_e new-value clear-cb!]
                      (on-change! new-value)
                      (when clear-cb!
                        (clear-cb!)))
      :drag-options {:hide-drag-area? true}
      :options      options}]]])

(defn no-results-found
  []
  [:div.no-results__container.mt-4
   [:img {:src "/img/undraw_no_data_qbuo.svg"}]
   [:div (loc/tr :no-results-found)]])

(defn resolve-activity-entities
  [activity-entity-types reverse-relationship-datas-by-entity-type]
  (->> activity-entity-types
       (mapcat #(get reverse-relationship-datas-by-entity-type %))
       (group-by (fn [[_ _ entity]]
                   (:db/id entity)))
       (map (fn [[_eid entity-datas]]
              (first entity-datas)))
       (sort-by (fn [[_ _ entity]]
                  (or (:note-call/timestamp entity)
                      (:note-meeting/timestamp entity)
                      (:note-message/timestamp entity)
                      (:email/date-ts entity)
                      ;;can be removed when date-ts migration has been run:
                      (when-let [dt (:email/date entity)]
                        (let [parse-date (dates/date dt {:pattern date-formats/rfc-2822})
                              js-date (dates/to-native parse-date)]
                          (dg/date->millis js-date)))
                      (:common/created-on entity)
                      (:common/updated-on entity)
                      0)))
       (reverse)))

(defn view
  [primary-entity-id state show-more! local-entities reverse-relationship-datas-by-entity-type
   activity-entity-types]
  (let [tenant-schema @session/tenant-schema
        resolved-entities @(r/track resolve-activity-entities
                                    activity-entity-types
                                    reverse-relationship-datas-by-entity-type)
        filtered-items (if (empty? (:selected-tags @state))
                         resolved-entities
                         (->> resolved-entities
                              (filter (fn [[_ _ entity]]
                                        (some (:selected-tags @state)
                                              (:activity/tag-tags entity))))))
        removed-entity-ids (some->> @local-entities
                                    (vals)
                                    (filter :common/disabled?)
                                    (map :db/id))
        filtered-items (some->> filtered-items
                                (remove (fn [i]
                                          (some #{(:db/id i)} (set removed-entity-ids)))))
        item-count (count filtered-items)
        show-count (:show-count @state)
        show-all? (or (< item-count 11)
                      (and show-count
                           (>= show-count item-count)))
        show-count (or show-count 5)
        visible-items (if show-all?
                        filtered-items
                        (take show-count
                              filtered-items))
        last-data (last visible-items)
        activity-tag-options (fu/named-value-options (registry/get-field-schema tenant-schema
                                                                                :activity/tag-tags)
                                                     nil)
        set-tag-filter! (fn [new-value]
                          (swap! state assoc :selected-tags new-value))

        activities
        (->> visible-items
             (remove (fn [[entity-type _ entity]]
                       (and (= entity-type :entity.type/value-creation)
                            (:value-creation/status entity)
                            (not= :value-creation.status/done (:value-creation/status entity))))))
        value-creations (->> visible-items
                             (map (fn [[_ _ entity]]
                                    entity))
                             (cu/only-entity :entity.type/value-creation)
                             (remove (fn [vce]
                                       (= :value-creation.status/done
                                          (:value-creation/status vce)))))
        visible-value-creations? (or (->> value-creations
                                          (count)
                                          (< 0))
                                     (->> activities
                                          (keep (fn [[_ _ entity]]
                                                  (when (= (:entity/type entity)
                                                           :entity.type/value-creation)
                                                    entity)))
                                          (count)
                                          (< 0)))
        prev-visible-value-creations? (:visible-value-creations? @state)
        _ (when-not (= visible-value-creations? prev-visible-value-creations?)
            (swap! state assoc :visible-value-creations? visible-value-creations?))
        activity-has-tag-field? (session/activity-has-tag-field?)]
    [:div
     (when activity-has-tag-field?
       [filter-by-tags-dropdown activity-tag-options state set-tag-filter!])
     (if (and activity-has-tag-field?
              (empty? filtered-items))
       [no-results-found]
       [:ul
        {:class "pt-3 flex flex-col "}
        (doall
         (for [entity value-creations]
           ^{:key (str "value-creation-" (:db/id entity))}
           [value-creation-elem
            {:state            state
             :local-entities   local-entities
             :viewed-entity-id primary-entity-id
             :entity           entity
             :class-name       ""}]))
        (doall
         (for [[_ _ entity :as item-data] activities]
           (if (= :entity.type/value-creation (:entity/type entity))
             ^{:key (str "value-creation-elem-" (:db/id entity))}
             [value-creation-elem
              {:state            state
               :local-entities   local-entities
               :viewed-entity-id primary-entity-id
               :entity           entity
               :class-name       ""}]
             ^{:key (str "activity-elem-" (:db/id entity))}
             [activity-elem
              {:state            state
               :local-entities   local-entities
               :viewed-entity-id primary-entity-id
               :entity           entity
               :class-name       (when (= item-data last-data)
                                   :last-item)}])))
        [:div.activity-entry.footer
         (when (and (not show-all?)
                    (> item-count show-count))
           [:div.activity-entry-left.clickable
            {:on-click show-more!}
            [:span
             (loc/tr :show-more)]])]])]))
